import React, { useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import * as S from "./AdminLayout.styled";
import logo from "assets/logo.png";
import { adminAuthService, AdminMeResponse } from "services/adminAuthService";

/* ─── 네비게이션 데이터 ───────────────────────────────────────── */

interface NavChild {
  label: string;
  icon: string;
  path: string;
}

interface NavItemDef {
  label: string;
  icon: string;
  path: string;
  children?: NavChild[];
}

interface NavSectionDef {
  section: string;
  items: NavItemDef[];
}

const NAV_ITEMS: NavSectionDef[] = [
  {
    section: "메인",
    items: [
      { label: "대시보드", icon: "📊", path: "/admin" },
    ],
  },
  {
    section: "운영 관리",
    items: [
      {
        label: "공간 관리",
        icon: "🏗️",
        path: "/admin/space",
        children: [
          { label: "시설 관리", icon: "🏛️", path: "/admin/facilities" },
          { label: "건물 관리", icon: "🏢", path: "/admin/buildings" },
          { label: "테마 관리", icon: "🎨", path: "/admin/themes" },
        ],
      },
      { label: "학과 관리", icon: "🎓", path: "/admin/departments" },
      { label: "회원 관리", icon: "👥", path: "/admin/members" },
      { label: "예약 관리", icon: "📅", path: "/admin/reservations" },
    ],
  },
  {
    section: "콘텐츠",
    items: [
      { label: "공지사항", icon: "📢", path: "/admin/notices" },
      { label: "문의 관리", icon: "💬", path: "/admin/inquiries" },
    ],
  },
  {
    section: "계정",
    items: [
      { label: "가입 요청 관리", icon: "👤", path: "/admin/signup-requests" },
    ],
  },
];

const PAGE_TITLES: Record<string, string> = {
  "/admin": "대시보드",
  "/admin/facilities": "시설 관리",
  "/admin/buildings": "건물 관리",
  "/admin/themes": "테마 관리",
  "/admin/space": "공간 관리",
  "/admin/reservations": "예약 관리",
  "/admin/members": "회원 관리",
  "/admin/departments": "학과 관리",
  "/admin/notices": "공지사항 관리",
  "/admin/inquiries": "문의 관리",
  "/admin/signup-requests": "가입 요청 관리",
};

/* ─── 컴포넌트 ───────────────────────────────────────────────── */

const AdminLayout: React.FC = () => {
  const location = useLocation();
  const [adminInfo, setAdminInfo] = useState<AdminMeResponse | null>(null);

  // 현재 경로가 자식 경로에 해당하는 부모는 자동 확장
  const [openParents, setOpenParents] = useState<Set<string>>(() => {
    const initial = new Set<string>();
    NAV_ITEMS.forEach((section) =>
      section.items.forEach((item) => {
        if (item.children?.some((c) => location.pathname.startsWith(c.path))) {
          initial.add(item.path);
        }
      })
    );
    return initial;
  });

  const pageTitle = PAGE_TITLES[location.pathname] ?? "관리자";

  useEffect(() => {
    adminAuthService.getMe()
      .then((info) => {
        setAdminInfo(info);
        localStorage.setItem("adminUniversityId", String(info.university.id));
      })
      .catch(() => {
        adminAuthService.logout();
      });
  }, []);

  const toggleParent = (path: string) => {
    setOpenParents((prev) => {
      const next = new Set(prev);
      next.has(path) ? next.delete(path) : next.add(path);
      return next;
    });
  };

  const handleLogout = () => adminAuthService.logout();

  return (
    <S.Wrapper>
      {/* ── 사이드바 ── */}
      <S.Sidebar>
        <S.SidebarLogo to="/admin">
          <img src={logo} alt="지금 우리 학교는" />
          <S.SidebarBadge>ADMIN</S.SidebarBadge>
        </S.SidebarLogo>

        <div style={{ flex: 1, overflowY: "auto" }}>
          {NAV_ITEMS.map((section) => (
            <S.NavSection key={section.section}>
              <S.NavSectionLabel>{section.section}</S.NavSectionLabel>

              {section.items.map((item) => {
                const hasChildren = !!item.children?.length;
                const isOpen = openParents.has(item.path);

                if (hasChildren) {
                  return (
                    <React.Fragment key={item.path}>
                      <S.NavParent
                        isOpen={isOpen}
                        onClick={() => toggleParent(item.path)}
                      >
                        <S.NavIcon>{item.icon}</S.NavIcon>
                        {item.label}
                        <S.NavChevron isOpen={isOpen}>▶</S.NavChevron>
                      </S.NavParent>

                      <S.SubNavWrap isOpen={isOpen}>
                        {item.children!.map((child) => (
                          <S.SubNavItem key={child.path} to={child.path}>
                            <S.NavIcon>{child.icon}</S.NavIcon>
                            {child.label}
                          </S.SubNavItem>
                        ))}
                      </S.SubNavWrap>
                    </React.Fragment>
                  );
                }

                return (
                  <S.NavItem
                    key={item.path}
                    to={item.path}
                    end={item.path === "/admin"}
                  >
                    <S.NavIcon>{item.icon}</S.NavIcon>
                    {item.label}
                  </S.NavItem>
                );
              })}
            </S.NavSection>
          ))}
        </div>

        <S.SidebarBottom>
          <S.LogoutButton onClick={handleLogout}>
            <S.NavIcon>🚪</S.NavIcon>
            로그아웃
          </S.LogoutButton>
        </S.SidebarBottom>
      </S.Sidebar>

      {/* ── 메인 영역 ── */}
      <S.Main>
        <S.TopBar>
          <S.PageTitle>{pageTitle}</S.PageTitle>
          <S.AdminInfo>
            <S.AdminAvatar>👤</S.AdminAvatar>
            <div>
              <S.AdminName>{adminInfo?.name ?? "—"}</S.AdminName>
              {adminInfo && (
                <S.AdminUniversity>{adminInfo.university.name}</S.AdminUniversity>
              )}
            </div>
          </S.AdminInfo>
        </S.TopBar>

        <S.Content>
          <Outlet />
        </S.Content>
      </S.Main>
    </S.Wrapper>
  );
};

export default AdminLayout;
